<script setup>
import { ref, computed } from 'vue';
import { Head } from '@inertiajs/vue3';
import PrimaryButton from "@/Components/PrimaryButton.vue";
import TextInput from "@/Components/TextInput.vue";
import AppLayout from "@/Layouts/AppLayout.vue";
import MapButton from "@/Components/MapButton.vue";
import Event from "@/Components/Dashboard/Event.vue";

const props = defineProps({
    federations: {
        type: Array,
        default: []
    },
    texte: {
        type: Object,
        default: {}
    },
    imagini: {
        type: Object,
        default: {}
    }
});

const searchQuery = ref('');

// Computed property to filter federations based on the search query
const filteredFederations = computed(() => {
    if (!searchQuery.value) return props.federations;
    return props.federations.filter(federation =>
        federation.name.toLowerCase().includes(searchQuery.value.toLowerCase())
    );
});

// State to track which federation's accordion is expanded
const expandedFederationId = ref(null);

// Toggle accordion for a federation
const toggleAccordion = (federationId) => {
    expandedFederationId.value = (expandedFederationId.value === federationId) ? null : federationId;
};
</script>


<template>
  <Head title="Satul Sportiv #BeActive — pașaportul tău de activități" />

  <div class="home min-h-screen text-gray-900" style="background-color: #f6dd00;">
    <a class="skip-link" href="#main">Sari la conținutul principal</a>

  <!-- Header -->
  <!-- Navbar -->
    <header class="flex justify-between items-center p-6">
      <img class="h-12 w-auto" src="/img/logo-2026.png" alt="Romania Be Active" />
      <nav class="space-x-12 hidden md:flex navbar" aria-label="Navigație principală">
        <a href="#beactive" class="text-lg transition-colors" style="color: inherit;" onmouseover="this.style.color='#1f2937'" onmouseout="this.style.color='inherit'">Romania Be Active 2026</a>
        <a href="#despre" class="text-lg transition-colors" style="color: inherit;" onmouseover="this.style.color='#1f2937'" onmouseout="this.style.color='inherit'">Despre app</a>
        <a href="#sustenabilitate" class="text-lg transition-colors" style="color: inherit;" onmouseover="this.style.color='#1f2937'" onmouseout="this.style.color='inherit'">Sustenabilitate</a>
        <!-- <a href="#parteneri" class="text-lg transition-colors" style="color: inherit;" onmouseover="this.style.color='#1f2937'" onmouseout="this.style.color='inherit'">Parteneri</a> -->
        <a href="#unde" class="text-lg transition-colors" style="color: inherit;" onmouseover="this.style.color='#1f2937'" onmouseout="this.style.color='inherit'">Unde</a>
      </nav>
      <a
        :href="route('login')"
        class="bg-transparent py-2 px-4 text-lg transition-all rounded-lg"
        style="border: 2px solid #1f2937; color: #1f2937;"
        onmouseover="this.style.backgroundColor='#1f2937'; this.style.color='#f6dd00'"
        onmouseout="this.style.backgroundColor='transparent'; this.style.color='#1f2937'"
        >Intră in app</a
      >
    </header>

    <main id="main" tabindex="-1">

    <!-- Hero Section -->
    <section
      class="hero-section relative flex flex-col justify-center items-center text-center space-y-10 py-20 pt-32 px-16 bg-cover h-[500px]"
      style="background-image: url('/img/hero.jpg'); background-position: center 40%;" id="despre"
    >
        <div class="relative z-10">
          <h1 class="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white font-oswald-bold mb-6">
              {{ props.texte['Hero Title'] || 'Satul Sportiv #BeActive' }}
          </h1>
          <div class="text-hero">
            <p class="text-lg md:text-xl pr-8 pl-6 text-white font-source">
              {{props.texte['Hero Text']}}
            </p>
          </div>
          <div class="flex flex-col sm:flex-row gap-4 sm:gap-4 justify-center items-center mt-8">
          <a href="/register" class="w-full sm:w-auto">
            <button
              class="w-full sm:w-auto text-gray-900 font-bold py-3 px-6 sm:px-8 rounded-md uppercase primary-background hover:opacity-90 transition-opacity"
              style="background-color: #f6dd00;"
            >
              Vreau pașaport
            </button>
          </a>
          <a href="#federatii" class="w-full sm:w-auto">
            <button
              class="w-full sm:w-auto bg-transparent font-bold py-3 px-6 sm:px-8 rounded-md uppercase transition-all hover:text-gray-900"
              style="border: 2px solid #f6dd00; color: #f6dd00;"
              onmouseover="this.style.backgroundColor='#f6dd00'; this.style.color='#1f2937'"
              onmouseout="this.style.backgroundColor='transparent'; this.style.color='#f6dd00'"
            >
              Vezi activități
            </button>
          </a>
          </div>
        </div>
    </section>

    <section id="beactive">


      <div class="bg-white py-12 px-6">
        <div class="max-w-7xl mx-auto text-center">
          <!-- Heading -->
          <h2 v-if="props.texte['Heading']" class="text-3xl md:text-4xl font-extrabold text-gray-800">
              {{props.texte['Heading']}}
          </h2>
          <!-- Paragraph -->
          <p class="mt-4 text-lg md:text-xl text-gray-600">
                {{props.texte['Text Heading']}}
          </p>
        </div>
      </div>
    </section>

    <section class="geometric-section-1 relative bg-gray-900 py-20 px-6 overflow-hidden">
      <div class="max-w-7xl mx-auto relative z-10">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <!-- Content -->
          <div class="text-white">
            <h2 class="text-4xl md:text-5xl font-extrabold font-oswald-bold mb-6" style="color: #f6dd00;">
              ACTIVITĂȚI
            </h2>
            <p class="text-lg md:text-xl text-gray-300 leading-relaxed">
              {{ props.texte['Box stanga Text'] }}
            </p>
          </div>
          <!-- Image -->
          <div class="relative">
            <img
              class="w-full h-80 object-cover rounded-lg shadow-2xl"
              :src="props.imagini['imagine stanga']"
              alt="Activități"
            />
          </div>
        </div>
      </div>
      <div class="geometric-cut-bottom"></div>
    </section>

    <section class="geometric-section-2 relative bg-gray-800 py-20 px-6 overflow-hidden">
      
      <div class="max-w-7xl mx-auto relative z-10">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <!-- Image (on left for this section) -->
          <div class="relative order-2 lg:order-1">
            <img
              class="w-full h-80 object-cover rounded-lg shadow-2xl"
              :src="props.imagini['imagine dreapta']"
              alt="Participanți"
            />
          </div>
          <!-- Content -->
          <div class="text-white order-1 lg:order-2">
            <h2 v-if="props.texte['Box dreapta titlu']" class="text-4xl md:text-5xl font-extrabold font-oswald-bold mb-6" style="color: #f6dd00;">
              {{props.texte['Box dreapta titlu']}}
            </h2>
            <p class="text-lg md:text-xl text-gray-300 leading-relaxed">
              {{props.texte['Box dreapta text']}}
            </p>
          </div>
        </div>
      </div>
    </section>

    <section id="sustenabilitate" class="sustainability-section relative py-20 px-6">
      <div class="max-w-7xl mx-auto relative z-10">
        <div class="text-center">
          <h2 class="text-4xl md:text-5xl font-extrabold font-oswald-bold mb-6 text-white">
            SUSTENABILITATE
          </h2>
          <p class="text-lg md:text-xl text-white leading-relaxed max-w-4xl mx-auto mb-8">
            Promovăm un stil de viață activ și sustenabil, protejând mediul înconjurător pentru generațiile viitoare prin sport și activități în aer liber.
          </p>
          <p class="text-lg md:text-xl text-white leading-relaxed max-w-4xl mx-auto">
            Romania Be Active este primul eveniment sportiv național cu raport CO₂ în timp real, realizat prin platforma <a href="https://seeo2.app" target="_blank" rel="noopener" class="text-white font-bold underline transition-colors">SeeO2<span class="sr-only"> (se deschide într-o filă nouă)</span></a>, oferind transparență și acuratețe în măsurarea amprentei de carbon. Această inițiativă devine fundament pentru edițiile viitoare, asigurând comparabilitate și sprijinind reducerea emisiilor, iar prin abordarea sa inovatoare reprezintă un exemplu de bune practici la nivel european, demonstrând că sportul și sustenabilitatea pot construi împreună un viitor mai responsabil.
          </p>
        </div>
      </div>
    </section>

    <!-- HIDDEN: Zona parteneri/federatii - decomentează când e gata
    <section class="bg-white" id="parteneri">

    <h2 class="font-semibold text-xl leading-tight bg-transparent">
        Lista Partenerilor
    </h2>


      <div>
          <div class="max-w-7xl mx-auto sm:px-4 lg:px-4 py-6">
              <div class="bg-white overflow-hidden shadow-lg sm:rounded-lg p-6">
                  <div>
                      <h3 class="text-lg font-semibold mb-4">Lista Partenerilor</h3>

                      <div class="mb-6">
                          <label for="cauta-parteneri" class="sr-only">Caută parteneri</label>
                          <TextInput
                              id="cauta-parteneri"
                              v-model="searchQuery"
                              type="search"
                              placeholder="Caută parteneri..."
                              class="w-full text-gray-700"
                          />
                      </div>

                      <div v-if="filteredFederations.length" class="max-h-[660px] overflow-y-auto">
                          <div
                              v-for="federation in filteredFederations"
                              :key="federation.id"
                              class="mb-4 p-4 bg-gray-100 rounded-xl"
                          >
                              <button
                                  type="button"
                                  class="flex w-full items-center justify-between text-left"
                                  :aria-expanded="expandedFederationId === federation.id ? 'true' : 'false'"
                                  :aria-controls="`federatie-${federation.id}`"
                                  @click="toggleAccordion(federation.id)">
                                  <div class="flex items-center">
                                      <div class="mr-8">
                                          <img v-if="federation.profile_photo_path" :src="'/storage/' + federation.profile_photo_path" :alt="`Sigla ${federation.name}`"
                                              class="h-16 w-16 rounded-full object-cover"/>
                                          <div v-else
                                              class="h-16 w-16 bg-gray-300 rounded-full flex items-center justify-center text-gray-600">
                                              <span>{{ federation.name.charAt(0) }}</span>
                                          </div>
                                      </div>

                                      <div>
                                          <p class="font-semibold text-lg text-gray-700">{{ federation.name }}</p>
                                      </div>
                                  </div>

                                  <div>
                                      <i v-if="expandedFederationId === federation.id"
                                        class="fas fa-chevron-up text-gray-700" aria-hidden="true"></i>
                                      <i v-else class="fas fa-chevron-down text-gray-700" aria-hidden="true"></i>
                                  </div>
                              </button>

                              <div v-if="expandedFederationId === federation.id"
                                  :id="`federatie-${federation.id}`"
                                  class="mt-4 bg-gray-50 p-4 rounded-lg">
                                  <h3 class="font-semibold text-md mb-2 text-black">Activități</h3>
                                  <div v-if="federation.events && federation.events.length" >
                                      <div v-for="event in federation.events" :key="event.id" class="flex items-center py-4">
                                          <div>
                                              <p class="font-semibold text-gray-700">{{ event.name }}</p>
                                              <p class="text-sm text-gray-700 py-3"
                                                v-html="event.description"></p>
                                              <div class="flex">
                                                  <p class="text-sm text-gray-600 mr-8">
                                                      <i class="fal fa-people w-6" aria-hidden="true"></i>
                                                      <span class="sr-only">Participanți:</span>
                                                      {{ event.participant_count }}
                                                  </p>
                                                  <p class="text-sm text-gray-600">
                                                      <i class="fal fa-calendar w-6" aria-hidden="true"></i>
                                                      <span class="sr-only">Data:</span>
                                                      {{ $moment(event.date_start).format('DD MMM YYYY HH:mm') }}
                                                  </p>
                                              </div>
                                          </div>
                                      </div>
                                  </div>
                                  <div v-else>
                                      <p class="text-gray-500">Nu există activități disponibile.</p>
                                  </div>
                              </div>
                          </div>
                      </div>

                      <div v-else>
                          <p class="text-gray-500">Nu s-au găsit parteneri.</p>
                      </div>
                  </div>
              </div>
          </div>
      </div>
    </section>
    -->

    <!-- Banner Parteneri -->
    <section class="bg-white py-12">
      <div class="max-w-7xl mx-auto px-6">
        <div class="flex justify-center">
          <img 
            src="/img/parteneri.png" 
            alt="Partenerii Romania Be Active" 
            class="max-w-full h-auto"
          />
        </div>
      </div>
    </section>

    <section id="unde" class="bg-white py-12" aria-labelledby="unde-title">
      <div class="max-w-4xl mx-auto text-center px-6 mb-8">
        <h2 id="unde-title" class="text-3xl md:text-4xl font-extrabold text-gray-800 mb-6">Unde ne vedem?</h2>
        <div class="text-lg md:text-xl text-gray-700 space-y-2">
          <p class="font-semibold">📅 26 septembrie</p>
          <p class="font-semibold">📍 Parcul Lumea Copiilor, București</p>
        </div>
        <p class="mt-4 text-gray-600">
          Participarea este GRATUITĂ, pe bază de înscriere și în limita locurilor disponibile. 
          Materialele necesare organizării evenimentului vor fi asigurate de Agenția Națională pentru Sport, în limita locurilor disponibile.
        </p>
        <p class="mt-4 text-gray-700">
          Harta de mai jos arată locația. Dacă nu o poți folosi,
          <a
            class="underline font-semibold"
            href="https://www.google.com/maps/search/?api=1&query=Parcul+Lumea+Copiilor+Bucuresti"
            target="_blank"
            rel="noopener noreferrer"
          >deschide Parcul Lumea Copiilor în Google Maps<span class="sr-only"> (se deschide într-o filă nouă)</span></a>.
        </p>
        <div class="mt-6">
          <a
            href="/img/Harta.pdf"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center bg-gray-900 text-white font-bold py-3 px-6 rounded-md uppercase hover:bg-gray-700 transition-colors"
          >
            <i class="fas fa-map mr-2" aria-hidden="true"></i>
            Descarcă harta Satului Sportiv
            <span class="sr-only"> (se deschide într-o filă nouă)</span>
          </a>
        </div>
      </div>
      <iframe
        title="Hartă Google cu Parcul Lumea Copiilor, locul de desfășurare. Adresa este scrisă mai sus, deasupra hărții."
        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2850.3211089267925!2d26.11473867732684!3d44.40605610341287!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x40b1fe57b976124f%3A0xbdae09dd30987fc5!2sLumea%20Copiilor%20Park!5e0!3m2!1sen!2sro!4v1788761150786!5m2!1sen!2sro"
        width="100%"
        height="600px"
        style="border:0;"
        allowfullscreen=""
        loading="lazy"
        referrerpolicy="strict-origin-when-cross-origin"
      ></iframe>
    </section>

    </main>

    <MapButton />

    <!-- Footer -->
    <footer class="text-white py-10" style="background-color: #15803d;">
      <div class="container mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl">

        <div class="logouri">
          <img src="/img/ce.png" alt="Comisia Europeană" class="mb-4">
          <img src="/img/GOV1.png" alt="Guvernul României — Ministerul Sportului">
        </div>


        <div>
          <ul>
            <li><a href="/terms-of-service" class="underline">GDPR</a></li>
            <li><a href="/privacy-policy" class="underline">Confidențialitatea datelor</a></li>
            <li><a href="/declaratie-accesibilitate" class="underline">Declarație de accesibilitate</a></li>
          </ul>
        </div>


        <div>
          <p>Telefon: +40 123 456 789</p>
          <p>Email: comunicare@sport.gov.ro</p>
          <p>Adresă: Str. Vasile Conta 16, București</p>
        </div>
      </div>
    </footer>

  </div>
</template>
